import { usePageContent } from "@/hooks/usePageContent";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import {
  FaWhatsapp, FaInstagram, FaFacebook, FaYoutube,
  FaTiktok, FaLinkedin, FaTelegramPlane, FaPinterest,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import {
  Globe, Calendar, ArrowRight, ExternalLink,
  BookOpen, Heart, Users, Phone, MapPin, Star,
  ShoppingBag, Info, FileText, Stethoscope, Leaf, Sparkles,
} from "lucide-react";

export type SocialTypeId =
  | "whatsapp" | "instagram" | "facebook" | "youtube"
  | "tiktok" | "linkedin" | "twitter" | "telegram" | "pinterest";

export interface SocialTypeDef {
  id: SocialTypeId;
  label: string;
  Icon: React.ElementType;
  color: string; // tailwind classes for admin badge
  defaultUrl: string;
}

export const SOCIAL_TYPES: SocialTypeDef[] = [
  { id: "whatsapp",  label: "WhatsApp",  Icon: FaWhatsapp,      color: "bg-green-100 text-green-700 border-green-200",   defaultUrl: "https://wa.me/" },
  { id: "instagram", label: "Instagram", Icon: FaInstagram,     color: "bg-pink-100 text-pink-600 border-pink-200",      defaultUrl: "https://instagram.com/" },
  { id: "facebook",  label: "Facebook",  Icon: FaFacebook,      color: "bg-blue-100 text-blue-700 border-blue-200",      defaultUrl: "https://facebook.com/" },
  { id: "youtube",   label: "YouTube",   Icon: FaYoutube,       color: "bg-red-100 text-red-600 border-red-200",         defaultUrl: "https://youtube.com/" },
  { id: "tiktok",    label: "TikTok",    Icon: FaTiktok,        color: "bg-gray-100 text-gray-800 border-gray-300",      defaultUrl: "https://tiktok.com/@" },
  { id: "linkedin",  label: "LinkedIn",  Icon: FaLinkedin,      color: "bg-sky-100 text-sky-700 border-sky-200",         defaultUrl: "https://linkedin.com/in/" },
  { id: "twitter",   label: "X (Twitter)", Icon: FaXTwitter,     color: "bg-gray-100 text-gray-800 border-gray-300",      defaultUrl: "https://x.com/" },
  { id: "telegram",  label: "Telegram",  Icon: FaTelegramPlane, color: "bg-blue-100 text-blue-500 border-blue-200",      defaultUrl: "https://t.me/" },
  { id: "pinterest", label: "Pinterest", Icon: FaPinterest,     color: "bg-red-100 text-red-500 border-red-200",         defaultUrl: "https://pinterest.com/" },
];

export function isSocialType(type: string): type is SocialTypeId {
  return SOCIAL_TYPES.some(s => s.id === type);
}

// ─── Nav button icon options ────────────────────────────────────────────────
export const NAV_ICONS: { id: string; label: string; Icon: React.ElementType }[] = [
  { id: "Calendar",     label: "Agenda",     Icon: Calendar },
  { id: "Globe",        label: "Site",       Icon: Globe },
  { id: "BookOpen",     label: "Blog",       Icon: BookOpen },
  { id: "Heart",        label: "Saúde",      Icon: Heart },
  { id: "Users",        label: "Equipe",     Icon: Users },
  { id: "Phone",        label: "Telefone",   Icon: Phone },
  { id: "MapPin",       label: "Local",      Icon: MapPin },
  { id: "Star",         label: "Destaque",   Icon: Star },
  { id: "ShoppingBag",  label: "Loja",       Icon: ShoppingBag },
  { id: "Info",         label: "Info",       Icon: Info },
  { id: "FileText",     label: "Artigo",     Icon: FileText },
  { id: "Stethoscope",  label: "Clínica",    Icon: Stethoscope },
  { id: "Leaf",         label: "Nutrição",   Icon: Leaf },
  { id: "Sparkles",     label: "Especial",   Icon: Sparkles },
];

export function getNavIcon(iconId?: string): React.ElementType {
  return NAV_ICONS.find(n => n.id === iconId)?.Icon ?? Globe;
}

export interface MobileLink {
  id: string;
  title: string;
  description?: string;
  url: string;
  type: SocialTypeId | "primary" | "default";
  icon?: string;
  is_active: boolean;
}

export const DEFAULT_LINKS: MobileLink[] = [
  { id: "1", title: "Agendar Consulta",  description: "Marque sua consulta online",    url: "/servicos",                                  type: "primary",   is_active: true },
  { id: "2", title: "WhatsApp",           description: "Fale comigo diretamente",        url: "https://wa.me/5511998874081",                type: "whatsapp",  is_active: true },
  { id: "3", title: "Instagram",          description: "@brenda.nutricionista",           url: "https://instagram.com/brenda.nutricionista", type: "instagram", is_active: true },
  { id: "4", title: "Blog de Nutrição",   description: "Dicas e receitas saudáveis",     url: "/blog",                                      type: "default",   is_active: true },
  { id: "5", title: "Nossos Serviços",    description: "Conheça o atendimento completo", url: "/servicos",                                  type: "default",   is_active: true },
];

function parseLinks(raw: string | undefined): MobileLink[] {
  if (!raw) return DEFAULT_LINKS;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_LINKS;
  } catch { return DEFAULT_LINKS; }
}

// ─── Icon per type ─────────────────────────────────────────────────────────
function LinkIcon({ link }: { link: MobileLink }) {
  const social = SOCIAL_TYPES.find(s => s.id === link.type);
  if (social) return <social.Icon size={18} className="text-white/80" />;
  const Icon = getNavIcon(link.icon ?? (link.type === "primary" ? "Calendar" : "Globe"));
  return <Icon className="w-[18px] h-[18px] text-white/80" />;
}

// ─── Single link row ────────────────────────────────────────────────────────
function LinkButton({ link }: { link: MobileLink }) {
  const isExternal = link.url.startsWith("http");
  const isWhatsapp = link.type === "whatsapp";

  const inner = (
    <div
      className={[
        "flex items-center gap-3 w-full px-4 py-4 rounded-2xl border transition-all duration-150 active:scale-[0.98]",
        isWhatsapp
          ? "bg-[#1a5230] border-white/20"
          : "bg-white/[0.08] border-white/[0.12]",
      ].join(" ")}
    >
      {/* Icon box */}
      <div
        className={[
          "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
          isWhatsapp ? "bg-white/20" : "bg-white/10",
        ].join(" ")}
      >
        <LinkIcon link={link} />
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0 text-left">
        <p className={["font-semibold text-sm leading-tight truncate", isWhatsapp ? "text-white" : "text-white/85"].join(" ")}>
          {link.title}
        </p>
        {link.description && (
          <p className="text-white/45 text-xs mt-0.5 truncate">{link.description}</p>
        )}
      </div>

      {/* Arrow */}
      {isExternal
        ? <ExternalLink className="w-3.5 h-3.5 text-white/30 shrink-0" />
        : <ArrowRight   className="w-3.5 h-3.5 text-white/30 shrink-0" />
      }
    </div>
  );

  if (isExternal) {
    return <a href={link.url} target="_blank" rel="noopener noreferrer">{inner}</a>;
  }
  return <a href={link.url}>{inner}</a>;
}

// ─── Main component ─────────────────────────────────────────────────────────
export function MobileLinkTree() {
  const { get }        = usePageContent("home");
  const { getSetting } = useSiteSettings();

  const heroImage    = (get("hero", "image")?.content_value?.url as string) ?? "/camisa-preta.JPG";
  const profilePhoto = getSetting("mobile_home_photo", "") || heroImage;
  const name         = getSetting("mobile_home_name",  "Dra. Brenda Ramos");
  const title        = getSetting("mobile_home_title", "Nutricionista CRN: 3/63492");
  const bio          = getSetting("mobile_home_bio",   "Especialista em nutrição clínica e emagrecimento saudável.");
  const activeLinks  = parseLinks(getSetting("mobile_home_links", "")).filter(l => l.is_active);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(180deg,#0e2d1c 0%,#1a4a2a 100%)" }}>

      {/* ── Hero photo ── */}
      <div className="relative h-64 shrink-0 overflow-hidden">
        <img src={heroImage} alt={name} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-[#0e2d1c]" />
      </div>

      {/* ── Profile ── */}
      <div className="-mt-14 z-10 flex flex-col items-center px-6 text-center">
        <div className="w-24 h-24 rounded-full border-[3px] border-white/25 shadow-xl overflow-hidden bg-[#1F5938] mb-3 shrink-0">
          <img src={profilePhoto} alt={name} className="w-full h-full object-cover object-top" />
        </div>
        <h1 className="text-xl font-bold text-white font-primary leading-tight">{name}</h1>
        <p className="text-white/55 text-xs mt-1 mb-2 tracking-wide">{title}</p>
        <p className="text-white/50 text-xs leading-relaxed max-w-[260px]">{bio}</p>
      </div>

      {/* ── Links ── */}
      <div className="px-5 pt-6 pb-8 space-y-4 z-10">
        {activeLinks.filter(l => !isSocialType(l.type)).map(link => <LinkButton key={link.id} link={link} />)}
      </div>

      {/* ── Footer ── */}
      <div className="pb-6 text-center">
        {/* Social icon row */}
        <div className="flex items-center justify-center gap-3 mb-2">
          {activeLinks.filter(l => isSocialType(l.type)).map(l => {
            const social = SOCIAL_TYPES.find(s => s.id === l.type);
            if (!social) return null;
            return (
              <a key={l.id} href={l.url} target="_blank" rel="noopener noreferrer" className="text-white/30 hover:text-white/60 transition-colors">
                <social.Icon size={16} />
              </a>
            );
          })}
        </div>
        <p className="text-[9px] uppercase tracking-[0.2em] text-white/20">
          <span className="text-[#C9A84C]/60">⚜</span> Clínica Equilíbrio
        </p>
      </div>
    </div>
  );
}
